import DeleteIcon from '@mui/icons-material/Close'
import EditIcon from '@mui/icons-material/Edit'
import CircularProgress from '@mui/material/CircularProgress'
import IconButton from '@mui/material/IconButton'
import { NativeVideoPlayer } from 'components/VideoPlayer'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setComposerVideoMetadata } from 'services/compose'
import { StoreThunkDispatch } from 'store/state'
import styles from './UploaderVideoPreview.pcss'

const VIDEO_LOAD_RETRY_COUNT = 10
const VIDEO_LOAD_RETRY_TIMEOUT = 2500

const LOADER_SIZE = 64
export const COMPOSER_VIDEO_ELEMENT_ID = 'composer-video'
interface UploaderVideoPreviewProps {
  mediaId: string
  videoUrl: string
  thumbnailUrl?: string
  className?: string
  onExpand: (url: string, selector: string) => void
  onDelete?: (id: string, url: string) => void
  onEdit?: (url: string) => void
}

export function UploaderVideoPreview(props: UploaderVideoPreviewProps) {
  const { mediaId, videoUrl, thumbnailUrl, className, onExpand, onDelete, onEdit } = props
  const dispatch = useDispatch<StoreThunkDispatch>()
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (videoUrl) {
      setLoading(true)
    }
  }, [videoUrl])

  const onLoaded = useCallback(() => {
    setLoading(false)
    dispatch(setComposerVideoMetadata(videoUrl))
  }, [videoUrl, dispatch])

  const onError = useCallback(() => {
    setLoading(false)
  }, [])

  const handleDelete = useCallback(() => {
    if (onDelete) {
      onDelete(mediaId, videoUrl)
    }
  }, [videoUrl, onDelete, mediaId])

  const handleEdit = useCallback(() => {
    if (onEdit) {
      onEdit(videoUrl)
    }
  }, [videoUrl, onEdit])

  const handleExpand = () => {
    onExpand(videoUrl, `[data-testid="${COMPOSER_VIDEO_ELEMENT_ID}"]`)
  }

  return (
    <div
      className={`${styles.wrapper} ${className || ''}`}
      data-testid="uploader-video-preview"
    >
      {loading && <div className={styles['loader-box']}><CircularProgress size={LOADER_SIZE} /></div>}
      <div className={`${styles.content} ${styles['content-expandable']}`} onClick={handleExpand}>
        <div className={styles.actions} onClick={e => e.stopPropagation()}>
          {onDelete && (
            <IconButton size="small" className={styles['btn-delete']} onClick={handleDelete}>
              <DeleteIcon className={styles.icon} />
            </IconButton>
          )}
          {onEdit && (
            <IconButton size="small" className={styles['btn-edit']} onClick={handleEdit}>
              <EditIcon className={styles.icon} />
            </IconButton>
          )}
        </div>
        <NativeVideoPlayer
          key={videoUrl}
          videoUrl={videoUrl}
          thumbnailUrl={thumbnailUrl}
          // Load metadata up front: the loader and the retry-until-available logic rely on canplay / error firing before play
          preload="metadata"
          togglePlayOnHover
          muted
          mediaElementId={COMPOSER_VIDEO_ELEMENT_ID}
          className={styles.video}
          playBtnClassName={styles['btn-play']}
          retry={{
            max: VIDEO_LOAD_RETRY_COUNT,
            timeout: VIDEO_LOAD_RETRY_TIMEOUT
          }}
          onError={onError}
          onCanPlay={onLoaded}
        />
      </div>
    </div>
  )
}
